import React from 'react';
import PropTypes from 'prop-types';
import { ConfirmationModal } from '@everfi/reactifi/src/components/ConfirmationModal';
import { i18n } from '@everfi/reactifi/src/i18n/translate';

const POLL_INTERVAL = 60000;
const TIMEOUT_WARNING_MS = 90000;
const SESSION_EXTENDING_EVENTS = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

export class AutoLogout extends React.Component {
  static propTypes = {
    modalBody: PropTypes.node,
    modalTitle: PropTypes.string,
    redirectUrl: PropTypes.string,
  };

  static defaultProps = {
    redirectUrl: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      logout: false,
      warning: false,
    };

    this.isMountedFlag = false;
    this.pollSessionStatus = null;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.checkAndUpdateSession();
    this.startPolling();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    this.stopPolling();
    this.removeEventListeners();
  }

  startPolling = () => {
    if (this.pollSessionStatus) return;

    this.pollSessionStatus = setInterval(() => {
      if (!this.state.logout) {
        this.checkAndUpdateSession();
      } else {
        this.stopPolling();
        window.location.reload();
      }
    }, POLL_INTERVAL);
  };

  stopPolling = () => {
    if (!this.pollSessionStatus) return;
    clearInterval(this.pollSessionStatus);
    this.pollSessionStatus = null;
  };

  safeSetState = nextState => {
    if (!this.isMountedFlag) return;
    this.setState(nextState);
  };

  checkAndUpdateSession = async () => {
    if (!this.isMountedFlag) return;

    let isExpired;
    let secondsLeft;
    try {
      const response = await fetch('/session_status', { credentials: 'same-origin' });
      const session = await response.json();
      isExpired = !session.active;
      secondsLeft = Number.isFinite(session.seconds_until_expiration) ? session.seconds_until_expiration : 0;
    } catch (e) {
      isExpired = true;
      secondsLeft = 0;
    }

    this.safeSetState({
      logout: isExpired,
      warning: !isExpired && secondsLeft * 1000 <= TIMEOUT_WARNING_MS,
    });
  };

  addEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.addEventListener(event, this.extendSession);
    });
  };

  removeEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.removeEventListener(event, this.extendSession);
    });
  };

  onWarningAction = () => {
    this.safeSetState({ warning: false });
    this.addEventListeners();
    this.extendSession();
  };

  redirect = () => {
    if (this.props.redirectUrl) {
      window.location.href = encodeURI(this.props.redirectUrl);
    } else {
      window.location.reload();
    }
  };

  extendSession = () => {
    this.removeEventListeners();
    window.location.reload();
  };

  get modalTitle() {
    return this.props.modalTitle || i18n.t('Your session has expired.');
  }

  get modalBody() {
    return (
      this.props.modalBody || (
        <div>{i18n.t('Unfortunately, your session has expired and we require you to login again.')}</div>
      )
    );
  }

  render() {
    return (
      <div>
        {this.state.logout && (
          <ConfirmationModal
            title={this.modalTitle}
            onConfirm={this.redirect}
            canClose={false}
            show={true}
            confirmOnly={true}
          >
            {this.modalBody}
          </ConfirmationModal>
        )}
        {this.state.warning && (
          <ConfirmationModal
            title={i18n.t('Your session is about to expire.')}
            onConfirm={this.onWarningAction}
            canClose={false}
            show={true}
            confirmOnly={true}
            confirmButtonText={i18n.t('Yes')}
          >
            {i18n.t('Would you like to stay logged in?')}
          </ConfirmationModal>
        )}
      </div>
    );
  }
}

export default AutoLogout;
